import PublicPageWidgets from "./components/PublicPageWidgets";
import React from "react";
import RouteLoadBoundary from "./components/RouteLoadBoundary";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

// Import pages
const Home = React.lazy(() => import("./pages/Home"));
const WoundAnalysis = React.lazy(() => import("./pages/WoundAnalysis"));
const History = React.lazy(() => import("./pages/History"));
const ClinicalForms = React.lazy(() => import("./pages/ClinicalForms"));
const ConsentForm = React.lazy(() => import("./pages/ConsentForm"));
const PatientFlow = React.lazy(() => import("./pages/PatientFlow"));
const EligibilityForm = React.lazy(() => import("./pages/EligibilityForm"));
const PatientRecordForm = React.lazy(() => import("./pages/PatientRecordForm"));
const SF36BeforeForm = React.lazy(() => import("./pages/SF36BeforeForm"));
const SF36AfterForm = React.lazy(() => import("./pages/SF36AfterForm"));
const EfficacyChecklistForm = React.lazy(() => import("./pages/EfficacyChecklistForm"));
const AdverseEventsForm = React.lazy(() => import("./pages/AdverseEventsForm"));
const Dashboard = React.lazy(() => import("./pages/Dashboard"));
const ClinicalDashboard = React.lazy(() => import("./pages/ClinicalDashboard"));
const ClinicalDashboardPage = React.lazy(() => import("./pages/ClinicalDashboardPage"));
const VRCalmDashboardPage = React.lazy(() => import("./pages/VRCalmDashboardPage"));
const AdminPage = React.lazy(() => import("./pages/AdminPage"));
const AdminLoginPage = React.lazy(() => import("./pages/AdminLoginPage"));
const LoginPage = React.lazy(() => import("./pages/LoginPage"));
const ChoosePlanPage = React.lazy(() => import("./pages/ChoosePlanPage"));
const RegisterPage = React.lazy(() => import("./pages/RegisterPage"));
const PricingPage = React.lazy(() => import("./pages/PricingPage"));
const PricingPageV2 = React.lazy(() => import("./pages/PricingPageV2"));
const WalletPage = React.lazy(() => import("./pages/WalletPage"));
const CheckoutSuccessPage = React.lazy(() => import("./pages/CheckoutSuccessPage"));
const MyProfilePage = React.lazy(() => import("./pages/MyProfilePage"));
const SolutionChatPage = React.lazy(() => import("./pages/SolutionChatPage"));
const SolutionsLeaderboardPage = React.lazy(() => import("./pages/SolutionsLeaderboardPage"));
const TherapySessionForm = React.lazy(() => import("./pages/TherapySessionForm"));
const OtrosProyectos = React.lazy(() => import("./pages/OtrosProyectos"));
const VRCalm = React.lazy(() => import("./pages/VRCalm"));

// Clinical Flow Component (VirgilioRays Protocol)
const ClinicalFlow = React.lazy(() => import("./components/ClinicalFlow"));
const ClinicalFlowDashboard = React.lazy(() => import("./components/ClinicalFlow").then(module => ({default:module.ClinicalFlowDashboard})));

// Virgilio Rays Hub Shell — 2 tabs: Clinical Trials + Virgilio Rays AI QuickScreen
const VirgilioRaysHubShell = React.lazy(() => import("./pages/hubs/VirgilioRaysHubShell"));

// Global Components





const TrustHospitalPage = React.lazy(() => import("./pages/solutions/TrustHospitalPage"));
const TrustHospitalEmbed = React.lazy(() => import("./pages/TrustHospitalEmbed"));
const VitalVisionPage = React.lazy(() => import("./pages/solutions/VitalVisionPage"));
const VRPatientVisionPage = React.lazy(() => import("./pages/solutions/VRPatientVisionPage"));
const HAMEPage = React.lazy(() => import("./pages/solutions/HAMEPage"));
const BHOGIPPage = React.lazy(() => import("./pages/solutions/BHOGIPPage"));

// Corporate Site Pages
const CorporateHome = React.lazy(() => import("./pages/corporate/CorporateHome"));
const CorporateSolutions = React.lazy(() => import("./pages/corporate/CorporateSolutions"));
const VigilioRaysPage = React.lazy(() => import("./pages/corporate/VigilioRaysPage"));
const SolutionDetailPage = React.lazy(() => import("./pages/corporate/SolutionDetailPage"));
const CorporateTechnology = React.lazy(() => import("./pages/corporate/CorporateTechnology"));
const CorporateEvidence = React.lazy(() => import("./pages/corporate/CorporateEvidence"));
const CorporateAbout = React.lazy(() => import("./pages/corporate/CorporateAbout"));
const CorporateContact = React.lazy(() => import("./pages/corporate/CorporateContact"));
const NTTDataPage = React.lazy(() => import("./pages/NTTDataPage"));
const SWCPage = React.lazy(() => import("./pages/SWCPage"));
const CorporateResources = React.lazy(() => import("./pages/corporate/CorporateResources"));
const AIDemosPage = React.lazy(() => import("./pages/corporate/AIDemosPage"));
const DashboardPage = React.lazy(() => import("./pages/corporate/DashboardPage"));
const FAQPage = React.lazy(() => import("./pages/corporate/FAQPage"));
const BlogPage = React.lazy(() => import("./pages/corporate/BlogPage"));
const PrivacyPolicyPage = React.lazy(() => import("./pages/corporate/PrivacyPolicyPage"));
const ComplianceSecurityPage = React.lazy(() => import("./pages/corporate/ComplianceSecurityPage"));
const EnterpriseHospitalsPage = React.lazy(() => import("./pages/corporate/EnterpriseHospitalsPage"));
const TermsOfUsePage = React.lazy(() => import("./pages/corporate/TermsOfUsePage"));
const CertificationsPage = React.lazy(() => import("./pages/corporate/CertificationsPage"));

// Subscription Contact Page
const ContactPage = React.lazy(() => import("./pages/ContactPage"));

// Orchestrator (Global AI Agent)
const OrchestratorPage = React.lazy(() => import("./pages/OrchestratorPage"));

// Interactive AI Demo Mode
const AIDemoMode = React.lazy(() => import("./pages/AIDemoMode"));

// Solution #24 & #25
const MedETechniScribePage = React.lazy(() => import("./pages/MedETechniScribePage"));
const PubMedETechniPage = React.lazy(() => import("./pages/PubMedETechniPage"));
const CommunityPage = React.lazy(() => import("./pages/CommunityPage"));
const CompleteProfilePage = React.lazy(() => import("./pages/CompleteProfilePage"));
const PublicProfilePage = React.lazy(() => import("./pages/PublicProfilePage"));
const BTriagePage = React.lazy(() => import("./pages/BTriagePage"));
import { AuthCallback } from "./components/SocialAuth";
const ReferralPage = React.lazy(() => import("./pages/ReferralPage"));
const MySolutionsPage = React.lazy(() => import("./pages/MySolutionsPage"));

// Solution Hub Pages
const DermaVisionHub = React.lazy(() => import("./pages/hubs").then(module => ({default:module.DermaVisionHub})));
const RadioVisionHub = React.lazy(() => import("./pages/hubs").then(module => ({default:module.RadioVisionHub})));
const LabVisionHub = React.lazy(() => import("./pages/hubs").then(module => ({default:module.LabVisionHub})));
const BTriageHub = React.lazy(() => import("./pages/hubs").then(module => ({default:module.BTriageHub})));
const PrognosAIHub = React.lazy(() => import("./pages/hubs").then(module => ({default:module.PrognosAIHub})));
const VRCalmHub = React.lazy(() => import("./pages/hubs").then(module => ({default:module.VRCalmHub})));
const BrainBeatsHub = React.lazy(() => import("./pages/hubs").then(module => ({default:module.BrainBeatsHub})));
const VRPatientVisionHub = React.lazy(() => import("./pages/hubs").then(module => ({default:module.VRPatientVisionHub})));
const TrustHospitalHub = React.lazy(() => import("./pages/hubs").then(module => ({default:module.TrustHospitalHub})));
const DocNotesHub = React.lazy(() => import("./pages/hubs").then(module => ({default:module.DocNotesHub})));
const FlowClinicHub = React.lazy(() => import("./pages/hubs").then(module => ({default:module.FlowClinicHub})));
const ZeroInfectionAIHub = React.lazy(() => import("./pages/hubs").then(module => ({default:module.ZeroInfectionAIHub})));
const PharmaKontactoHub = React.lazy(() => import("./pages/hubs").then(module => ({default:module.PharmaKontactoHub})));
const IngreVisionHub = React.lazy(() => import("./pages/hubs").then(module => ({default:module.IngreVisionHub})));
const NutriVisionHub = React.lazy(() => import("./pages/hubs").then(module => ({default:module.NutriVisionHub})));
const LucIAHub = React.lazy(() => import("./pages/hubs").then(module => ({default:module.LucIAHub})));
const BHOGIPHub = React.lazy(() => import("./pages/hubs").then(module => ({default:module.BHOGIPHub})));
const SignalVisionHub = React.lazy(() => import("./pages/hubs").then(module => ({default:module.SignalVisionHub})));
const SportmedAIHub = React.lazy(() => import("./pages/hubs").then(module => ({default:module.SportmedAIHub})));
const UVAseptixHub = React.lazy(() => import("./pages/hubs").then(module => ({default:module.UVAseptixHub})));
const PaperScriptAIHub = React.lazy(() => import("./pages/hubs").then(module => ({default:module.PaperScriptAIHub})));
const AnamarIAHub = React.lazy(() => import("./pages/hubs").then(module => ({default:module.AnamarIAHub})));
const VirgilioRaysHub = React.lazy(() => import("./pages/hubs").then(module => ({default:module.VirgilioRaysHub})));
const HAMEHub = React.lazy(() => import("./pages/hubs").then(module => ({default:module.HAMEHub})));
const VitalVisionHub = React.lazy(() => import("./pages/hubs").then(module => ({default:module.VitalVisionHub})));
const ClinicAIHub = React.lazy(() => import("./pages/hubs").then(module => ({default:module.ClinicAIHub})));
const OdontoAIHub = React.lazy(() => import("./pages/hubs").then(module => ({default:module.OdontoAIHub})));
const MedReferHub = React.lazy(() => import("./pages/hubs").then(module => ({default:module.MedReferHub})));
const MedDirectoryHub = React.lazy(() => import("./pages/hubs").then(module => ({default:module.MedDirectoryHub})));

function GCalCallback() {
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    if (code && window.opener) {
      window.opener.postMessage({ type: 'gcal-callback', code, state }, '*');
      window.close();
    }
  }, []);
  return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#0a0f1a', color: '#fff', fontFamily: 'sans-serif' }}><p>Conectando Google Calendar... Puedes cerrar esta ventana.</p></div>;
}

function SmartAIDemoRedirect() {
  // If user already logged in (community session), send to /my-solutions.
  // Otherwise, send to /register.
  const hasSession = !!localStorage.getItem('communitySessionToken');
  return <Navigate to={hasSession ? '/my-solutions' : '/register'} replace />;
}

import PublicRouteMetadata from './components/PublicRouteMetadata';

function AppContent() {
  // Check URL fragment for session_id (from Emergent OAuth callback)
  if (window.location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <PublicRouteMetadata />
      {!window.location.pathname.startsWith('/ntt-data') && !/^\/swc\/?$/i.test(window.location.pathname) && <PublicPageWidgets />}

      <RouteLoadBoundary>
      <Routes>
        {/* MedETechni Corporate Website - Main Site */}
        <Route path="/" element={<CorporateHome />} />
          <Route path="/solutions" element={<CorporateSolutions />} />
          <Route path="/solutions/virgilio-rays" element={<VirgilioRaysHubShell />} />
          <Route path="/solutions/trust-hospital" element={<TrustHospitalPage />} />
          <Route path="/solutions/trusthospital" element={<TrustHospitalPage />} />
          <Route path="/trust/embed/:hospitalId" element={<TrustHospitalEmbed />} />
          <Route path="/solutions/vital-vision" element={<VitalVisionPage />} />
          <Route path="/solutions/vitalvision" element={<VitalVisionPage />} />
          <Route path="/solutions/vitavision" element={<VitalVisionPage />} />
          <Route path="/solutions/vr-patient-vision" element={<VRPatientVisionPage />} />
          <Route path="/solutions/vrpatientvision" element={<VRPatientVisionPage />} />
          <Route path="/solutions/hame-info" element={<HAMEPage />} />
          <Route path="/solutions/bhogip" element={<BHOGIPPage />} />
          <Route path="/solutions/:solutionId" element={<SolutionDetailPage />} />
          <Route path="/technology" element={<CorporateTechnology />} />
          <Route path="/evidence" element={<CorporateEvidence />} />
          <Route path="/about" element={<CorporateAbout />} />
          <Route path="/ntt-data" element={<NTTDataPage />} />
          <Route path="/SWC" element={<SWCPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/corporate/contact" element={<CorporateContact />} />
          <Route path="/demo" element={<ContactPage />} />
          <Route path="/subscribe" element={<ContactPage />} />
          <Route path="/resources" element={<CorporateResources />} />
          <Route path="/ai-demos" element={<SmartAIDemoRedirect />} />
          <Route path="/ai-demo" element={<SmartAIDemoRedirect />} />
          <Route path="/scribe" element={<MedETechniScribePage />} />
          <Route path="/pubmedetechni" element={<PubMedETechniPage />} />
          <Route path="/community" element={<CommunityPage />} />
          <Route path="/community/profile/:email" element={<PublicProfilePage />} />
          <Route path="/complete-profile" element={<CompleteProfilePage />} />
          <Route path="/my-solutions" element={<MySolutionsPage />} />
          <Route path="/btriage" element={<BTriagePage />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/verify/:certId" element={<DashboardPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/security" element={<ComplianceSecurityPage />} />
          <Route path="/compliance-security" element={<ComplianceSecurityPage />} />
          <Route path="/enterprise" element={<EnterpriseHospitalsPage />} />
          <Route path="/enterprise/hospitals" element={<EnterpriseHospitalsPage />} />
          <Route path="/hospitals" element={<EnterpriseHospitalsPage />} />
          <Route path="/terms" element={<TermsOfUsePage />} />
          <Route path="/certifications" element={<CertificationsPage />} />
          <Route path="/auth/google/callback" element={<AuthCallback />} />
          <Route path="/os/gcal-callback" element={<GCalCallback />} />

          {/* Legacy Corporate Routes (redirect compatible) */}
          <Route path="/corporate" element={<CorporateHome />} />
          <Route path="/corporate/solutions" element={<CorporateSolutions />} />
          <Route path="/corporate/solutions/virgilio-rays" element={<VigilioRaysPage />} />
          <Route path="/corporate/technology" element={<CorporateTechnology />} />
          <Route path="/corporate/evidence" element={<CorporateEvidence />} />
          <Route path="/corporate/about" element={<CorporateAbout />} />
          <Route path="/corporate/contact" element={<CorporateContact />} />
          <Route path="/corporate/demo" element={<CorporateContact />} />

          {/* Virgilio Rays Clinical Platform (Internal) */}
          {/* Platform routes - hidden, redirect to main */}
          <Route path="/platform" element={<Navigate to="/" replace />} />
          <Route path="/platform/*" element={<Navigate to="/" replace />} />
          <Route path="/solutions/vr-calm" element={<VRCalm />} />

          {/* Specific Dashboards - Virgilio Rays and VR Calm */}
          <Route path="/solutions/virgilio-rays/dashboard" element={<ClinicalDashboardPage />} />
          <Route path="/solutions/vr-calm/dashboard" element={<VRCalmDashboardPage />} />
          <Route path="/virgilio-rays/dashboard" element={<ClinicalDashboardPage />} />
          <Route path="/vr-calm/dashboard" element={<VRCalmDashboardPage />} />

          {/* Solution Hub Pages - All 23 AI Solutions */}
          <Route path="/solutions/dermavision" element={<DermaVisionHub />} />
          <Route path="/solutions/radiovision" element={<RadioVisionHub />} />
          <Route path="/solutions/labvision" element={<LabVisionHub />} />
          <Route path="/solutions/btriage" element={<BTriageHub />} />
          <Route path="/solutions/prognosai" element={<PrognosAIHub />} />
          <Route path="/solutions/vrcalm" element={<VRCalmHub />} />
          <Route path="/solutions/brain-beats" element={<BrainBeatsHub />} />
          <Route path="/solutions/vr-patient-vision" element={<VRPatientVisionHub />} />
          <Route path="/solutions/trusthospital" element={<TrustHospitalHub />} />
          <Route path="/solutions/docnotes" element={<DocNotesHub />} />
          <Route path="/solutions/flowclinic" element={<FlowClinicHub />} />
          <Route path="/solutions/zeroinfection-ai" element={<ZeroInfectionAIHub />} />
          <Route path="/solutions/pharmakontacto" element={<PharmaKontactoHub />} />
          <Route path="/solutions/ingrevision" element={<IngreVisionHub />} />
          <Route path="/solutions/nutrivision" element={<NutriVisionHub />} />
          <Route path="/solutions/lucia" element={<LucIAHub />} />
          <Route path="/solutions/bhogip" element={<BHOGIPHub />} />
          <Route path="/solutions/signalvision" element={<SignalVisionHub />} />
          <Route path="/solutions/sportmed-ai" element={<SportmedAIHub />} />
          <Route path="/solutions/uv-aseptix" element={<UVAseptixHub />} />
          <Route path="/solutions/paperscript-ai" element={<PaperScriptAIHub />} />
          <Route path="/solutions/anamarai" element={<AnamarIAHub />} />
          <Route path="/solutions/virgilio-rays-hub" element={<Navigate to="/solutions/virgilio-rays" replace />} />
          <Route path="/solutions/hame" element={<HAMEHub />} />
          <Route path="/solutions/vitavision-hub" element={<VitalVisionHub />} />
          <Route path="/solutions/clinicai" element={<ClinicAIHub />} />
          <Route path="/solutions/medrefer" element={<MedReferHub />} />
          <Route path="/solutions/meddirectory" element={<MedDirectoryHub />} />
          <Route path="/solutions/odontoai" element={<OdontoAIHub />} />

          {/* Clinical Flow - VirgilioRays Protocol */}
          <Route path="/clinical-flow" element={<ClinicalFlow />} />
          <Route path="/clinical-flow/dashboard" element={<ClinicalFlowDashboard />} />
          <Route path="/protocol/virgilio-rays" element={<ClinicalFlow />} />
          <Route path="/protocol/virgilio-rays/dashboard" element={<ClinicalFlowDashboard />} />

          {/* Admin Console (Protected, noindex) */}
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/admin/*" element={<AdminPage />} />
          <Route path="/os-access" element={<AdminLoginPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/choose-plan" element={<Navigate to="/pricing" replace />} />
          <Route path="/pricing" element={<PricingPageV2 />} />
          <Route path="/pricing-v2" element={<PricingPageV2 />} />
          <Route path="/pricing-legacy" element={<PricingPage />} />
          <Route path="/wallet" element={<WalletPage />} />
          <Route path="/profile" element={<MyProfilePage />} />
          <Route path="/my-profile" element={<MyProfilePage />} />
          <Route path="/chat/:solutionId" element={<SolutionChatPage />} />
          <Route path="/solutions/ranking" element={<SolutionsLeaderboardPage />} />
          <Route path="/ai/ranking" element={<SolutionsLeaderboardPage />} />
          <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
          <Route path="/subscription/success" element={<CheckoutSuccessPage />} />
          <Route path="/referrals" element={<ReferralPage />} />
          <Route path="/orchestrator" element={<OrchestratorPage />} />
        </Routes>
      </RouteLoadBoundary>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </div>
  );
}

export default App;
